import { Dropdown, Spin } from 'antd';
import { Icon } from 'Components';
import { Group } from 'Services/Api/Groups/Types';
import {
	usePropertiesFilter,
	PropertiesFilterProps
} from './PropertiesFilter.hooks';
import styles from './PropertiesFilter.module.scss';

interface PropertyRowProps {
	group: Group;
	selectedIds: number[];
	onToggleCheck: (id: number) => void;
}

function PropertyRow({ group, selectedIds, onToggleCheck }: PropertyRowProps) {
	const isSelected = selectedIds.includes(group.id);

	return (
		<div
			className={styles.propertyRow}
			onClick={() => onToggleCheck(group.id)}
		>
			<span className={styles.tickIcon}>
				{isSelected && <Icon type="check" role="img" />}
			</span>
			<span className={styles.rowLabel}>{group.name}</span>
		</div>
	);
}

function PropertiesFilter(props: PropertiesFilterProps) {
	const {
		isSuperAdmin,
		open,
		loadingRoot,
		loadingParentId,
		tenants,
		propertiesByParent,
		selectedIds,
		expandedTenants,
		nonAdminProperties,
		handleOpenChange,
		toggleTenant,
		toggleCheck,
		clearAll
	} = usePropertiesFilter(props);

	const triggerLabel =
		selectedIds.length === 0
			? 'All Properties'
			: `${selectedIds.length} Selected`;

	const overlay = (
		<div className={styles.panel} onMouseDown={(e) => e.preventDefault()}>
			<div className={styles.panelHeader}>
				<span className={styles.panelTitle}>Properties</span>
				{selectedIds.length > 0 && (
					<span className={styles.clearBtn} onClick={clearAll}>
						Clear
					</span>
				)}
			</div>

			<div className={styles.list}>
				{loadingRoot ? (
					<div className={styles.loadingWrap}>
						<Spin />
					</div>
				) : isSuperAdmin ? (
					<>
						{tenants.length === 0 && (
							<div className={styles.emptyText}>
								No tenants found
							</div>
						)}
						{tenants.map((tenant) => {
							const isExpanded = expandedTenants.has(tenant.id);
							const properties =
								propertiesByParent[tenant.id] ?? [];
							const isTenantLoading =
								loadingParentId === tenant.id;

							return (
								<div key={tenant.id}>
									<div
										className={styles.tenantRow}
										onClick={() => toggleTenant(tenant.id)}
									>
										<span className={styles.tenantName}>
											{tenant.name}
										</span>
										<span
											className={`${styles.expandBtn} ${
												isExpanded
													? styles.expandBtnOpen
													: ''
											}`}
										>
											{isTenantLoading ? (
												<Spin size="small" />
											) : (
												<Icon type="campaignDropdown" />
											)}
										</span>
									</div>
									{isExpanded && (
										<div>
											{properties.length === 0 &&
												!isTenantLoading && (
													<div
														className={
															styles.emptyText
														}
														style={{
															paddingLeft: 28
														}}
													>
														No properties
													</div>
												)}
											{properties.map((prop) => (
												<PropertyRow
													key={prop.id}
													group={prop}
													selectedIds={selectedIds}
													onToggleCheck={toggleCheck}
												/>
											))}
										</div>
									)}
								</div>
							);
						})}
					</>
				) : (
					<>
						{nonAdminProperties.length === 0 ? (
							<div className={styles.emptyText}>
								No properties found
							</div>
						) : (
							nonAdminProperties.map((prop) => (
								<PropertyRow
									key={prop.id}
									group={prop}
									selectedIds={selectedIds}
									onToggleCheck={toggleCheck}
								/>
							))
						)}
					</>
				)}
			</div>
		</div>
	);

	return (
		<Dropdown
			visible={open}
			onVisibleChange={handleOpenChange}
			overlay={overlay}
			trigger={['click']}
			placement="bottomRight"
		>
			<div className={styles.trigger}>
				<span className={styles.triggerLabel}>{triggerLabel}</span>
				{selectedIds.length > 0 && (
					<span className={styles.badge}>{selectedIds.length}</span>
				)}
				<span
					className={`${styles.triggerChevron} ${
						open ? styles.triggerChevronOpen : ''
					}`}
				>
					<Icon className={styles.icon} type="chevronDown" />
				</span>
			</div>
		</Dropdown>
	);
}

export default PropertiesFilter;
